import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

import { TextArea, Button } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { TalentBoardComment, TalentBoardEventType, TalentBoardPersistedEventType } from '@bloomays-lib/types.shared';

import useUpsertTalentBoardComment from '../../hooks/useUpsertTalentBoardComment';
import useTalentBoardEvents from '../../hooks/useTalentBoardEvents';
import { useTalentsForTalentBoardStore } from '../../stores/talentsForTalentBoardStore';
import { useTalentBoardStore } from '../../stores/talentBoardStore';
import { Logger } from '../../services/service-logger';

const logger = Logger('TalentBoardCommentForm');

type TalentBoardCommentFormProps = {
  talentId: string;
  jobId: string;
  isClient: boolean;
  comment?: TalentBoardComment;
  setIsEditing?: (isEditing: boolean) => void;
};

export const TalentBoardCommentForm = ({
  talentId,
  jobId,
  isClient,
  comment,
  setIsEditing,
}: TalentBoardCommentFormProps) => {
  const auth = useAuthStore((state) => state.auth);
  const { upsertTalentBoardComment } = useUpsertTalentBoardComment();
  const { createTalentBoardEvent } = useTalentBoardEvents();
  const { upsertTalentComments } = useTalentsForTalentBoardStore();
  const { upsertSelectedTalentComments, selectedTalent: talent } = useTalentBoardStore();
  const [newComment, setNewComment] = useState<string>(comment?.comment || '');
  const { t } = useTranslation(['talentBoard', 'random']);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newComment.trim() || !auth?.user?.email || !talent) {
      return;
    }

    logger.info('Traffic', {
      page: 'TalentProfile',
      evt: { name: TalentBoardEventType.TalentboardUpsertComment },
      job: {
        id: jobId,
      },
      talent: {
        id: talentId,
      },
    });
    const updatedComment = await upsertTalentBoardComment({
      id: comment?.id || null,
      talentId: talentId,
      jobId: jobId,
      comment: newComment.trim() || '',
      author: auth?.user?.email || '',
    });

    if (updatedComment) {
      upsertTalentComments(jobId, talentId, updatedComment);
      upsertSelectedTalentComments(updatedComment);
      setNewComment('');
      setIsEditing?.(false);

      void createTalentBoardEvent({
        jobId,
        date: new Date(),
        type: TalentBoardPersistedEventType.TalentboardUpsertComment,
        data: { talentId, talentFullName: talent.firstname + ' ' + talent.lastname },
      });
    }
  };

  return (
    <CommentForm onSubmit={handleSubmit}>
      <TextArea
        name="clientComment"
        handleChange={(e: { target: { value: string } }) => {
          setNewComment(e.target.value);
        }}
        value={newComment}
        placeholder={
          isClient
            ? t('talentJob.commentPlaceholderClient', { ns: 'talentBoard' })
            : t('talentJob.commentPlaceholder', { ns: 'talentBoard' })
        }
        required={true}
      />
      <ButtonsContainer>
        {setIsEditing && (
          <Button
            textButton={t('cancel', { ns: 'random' })}
            buttonType="button"
            color="secondary"
            onClick={() => setIsEditing(false)}
          />
        )}
        <Button textButton={t('register', { ns: 'random' })} buttonType="submit" />
      </ButtonsContainer>
    </CommentForm>
  );
};

const CommentForm = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
}));

const ButtonsContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',

  '&:has(> *:nth-of-type(2))': {
    justifyContent: 'space-between',
  },
}));
